import React, { useState, useEffect } from 'react';
import { Ban, Users, Shield, Eye, EyeOff, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlockedUsers = () => {
  const { token } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlocks();
  }, []);

  const fetchBlocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/moderation/blocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocks(response.data);
    } catch (error) {
      console.error('Error fetching blocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (blockId, username) => {
    try {
      await axios.delete(`${API_URL}/api/moderation/blocks/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${username || 'User'} has been unblocked`);
      fetchBlocks();
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleUpdateVisibility = async (blockId, newVisibility) => {
    try {
      await axios.put(
        `${API_URL}/api/moderation/blocks/${blockId}`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Block settings updated');
      fetchBlocks();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Ban className="w-8 h-8 text-primary" />
            Blocked Users
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage users you've blocked from interacting with you
          </p>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-muted/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">About blocking</p>
                <p className="text-muted-foreground mt-1">
                  Blocked users cannot send you messages, comment on your posts, or send friend requests. 
                  You can choose to also hide their content from your feed entirely.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Blocked Users List */}
        {blocks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No blocked users</h3>
              <p className="text-muted-foreground">
                When you block someone, they'll appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {blocks.map((block) => (
              <Card key={block.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>
                          {(block.blocked_display_name || block.blocked_username || 'U')[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {block.blocked_display_name || block.blocked_username || 'Unknown User'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          @{block.blocked_username || 'unknown'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {block.visibility === 'hide_completely' ? (
                              <><Eye className="w-3 h-3 mr-1" /> Hidden completely</>
                            ) : (
                              <><EyeOff className="w-3 h-3 mr-1" /> Interactions blocked</>
                            )}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Blocked {formatDistanceToNow(new Date(block.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateVisibility(
                          block.id,
                          block.visibility === 'hide_completely' ? 'hide_interactions' : 'hide_completely'
                        )}
                      >
                        {block.visibility === 'hide_completely' ? 'Show in feed' : 'Hide from feed'}
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Unblock {block.blocked_display_name || block.blocked_username}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              They will be able to see your profile and interact with your content again.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleUnblock(block.id, block.blocked_display_name || block.blocked_username)}
                            >
                              Unblock
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  
                  {block.reason && (
                    <p className="text-sm text-muted-foreground mt-3 pl-12">
                      <span className="font-medium">Your note:</span> {block.reason}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Summary */}
        {blocks.length > 0 && (
          <p className="text-sm text-muted-foreground text-center mt-6">
            {blocks.length} user{blocks.length !== 1 ? 's' : ''} blocked
          </p>
        )}
      </div>
    </div>
  );
};

export default BlockedUsers;
